import { Controller } from "stimulus";
import { EventPriceChartReload, EventCurrencyChanged } from "../events";
import { getActiveCurrency, formatCurrency } from "../util/currency";
import { stockChartOptions } from "../config";

// Action type
const ACTION_TYPE = {
  DURATION: "duration",
  TOGGLE_MODE: "toggle-mode",
  TYPE_PRICE: "type-price",
  TYPE_MARKET_CAP: "type-market-cap"
};

// Price vs market cap
const CHART_DATA_TYPE = {
  PRICE: "price",
  MARKET_CAP: "market-cap"
};

// Whether or not we display markers
const CHART_MODE = {
  CIRCLE: "circle",
  LINE: "line"
};

let Highcharts;

export default class extends Controller {
  static targets = [
    "chart",
    "spinner",
    "toolbar",
    "filterChart",
    "priceChartExportExcel",
    "priceChartExportCsv"
  ];

  connect() {
    $(this.spinnerTarget).show();

    // COMPONENT PROPERTIES
    this.chartDataType =
      this.chartTarget.getAttribute("data-chart-data-type") || CHART_DATA_TYPE.PRICE;
    this.chartDataUrl = this.chartTarget.getAttribute("data-initial-url");
    this.chartMode = this.chartTarget.getAttribute("data-chart-mode") || CHART_MODE.CIRCLE;

    this.isPortfolio = JSON.parse(this.chartTarget.getAttribute("data-is-portfolio"));

    Promise.all([
      import("highcharts/highstock"),
      import("highcharts/modules/exporting"),
      import("highcharts/modules/offline-exporting")
    ]).then(res => {
      Highcharts = res[0];
      const Exporting = res[1];
      const OfflineExporting = res[2];

      Exporting(Highcharts);
      OfflineExporting(Highcharts);

      this.renderChart(this.chartDataUrl, this.chartMode);

      window.addEventListener(EventCurrencyChanged, e => this._forceRerender(e));
      window.addEventListener(EventPriceChartReload, e => {
        if (e.detail && e.detail.range) {
          const $rangeButton = $(this.toolbarTarget).find(`.graph-stats-btn-${e.detail.range}`);
          $rangeButton.addClass("active");
          $rangeButton.siblings().removeClass("active");
          this.chartDataUrl = $rangeButton.data("graph-stats-url");
        }

        this._forceRerender(e);
      });
    });
  }

  _forceRerender(e) {
    this.renderChart(
      this.chartDataUrl,
      this.chartMode,
      Object.assign(
        {
          forceReload: true
        },
        e.detail || {}
      )
    );
  }

  renderChart(chartUrl, chartMode, options = {}) {
    // We try to remember previous values and don't update if nothing changed.
    if (
      !options.forceReload &&
      chartUrl === this._prevChartUrl &&
      chartMode === this._prevChartMode
    ) {
      $(this.spinnerTarget).hide();
      return;
    }
    this._prevChartMode = chartMode;
    this._prevChartUrl = chartUrl;

    $(this.spinnerTarget).show();

    const chartContainerId = this.chartTarget.id;
    const vsCurrency = this.chartTarget.getAttribute("data-vs-currency");

    const toggleBtn = this.filterChartTarget.querySelector(".chart-mode-toggle");
    if (toggleBtn) {
      const icon = toggleBtn.querySelector("i");
      if (chartMode === CHART_MODE.LINE) {
        icon.classList.add("fa-circle");
        icon.classList.remove("fa-chart-line");
        icon.style.fontSize = "7px"; // dot is too large!
      } else {
        icon.classList.add("fa-chart-line");
        icon.classList.remove("fa-circle");
        icon.style.fontSize = "14px";
      }
    }

    // Get the latest chart URL with the latest currency to be used
    const activeCurrencyCode = getActiveCurrency();
    chartUrl = chartUrl.replace(vsCurrency, activeCurrencyCode);

    let previousCurrency = this._prevCurrency ? this._prevCurrency : vsCurrency;
    try {
      let exportExcelButtons = this.priceChartExportExcelTarget;
      exportExcelButtons.href = exportExcelButtons.href.replace(
        previousCurrency,
        activeCurrencyCode
      );
      let exportCsvButtons = this.priceChartExportCsvTarget;
      exportCsvButtons.href = exportCsvButtons.href.replace(previousCurrency, activeCurrencyCode);
    } catch (error) {}

    this._prevCurrency = activeCurrencyCode;

    fetch(chartUrl, { credentials: "same-origin" })
      .then(response => response.json())
      .then(data => {
        const pricesData = data.stats;
        const totalVolumesData = data.total_volumes;

        const isPortfolio = this.isPortfolio;
        const chart = Highcharts.StockChart(
          chartContainerId,
          Object.assign(
            {
              yAxis: [
                {
                  showLastLabel: true,
                  opposite: false,
                  labels: {
                    style: {
                      color: "#56b4e9"
                    },
                    align: "left",
                    x: 0,
                    formatter() {
                      return formatCurrency(this.value, activeCurrencyCode);
                    }
                  },
                  height: "70%"
                },
                {
                  labels: {
                    style: {
                      color: "#fff"
                    },
                    align: "right"
                  },
                  height: "25%",
                  top: "75%",
                  offset: 0,
                  pointWidth: 1
                }
              ],
              tooltip: {
                formatter() {
                  let formatter =
                    `<b>${chart.time.dateFormat("%a %d %b %Y, %H:%M:%S", this.x)}</b><br/>` +
                    "<b>Price: </b>" +
                    formatCurrency(this.points[0].y, activeCurrencyCode);

                  if (this.points[1]) {
                    // This is not present sometimes.
                    formatter += "<br/>";
                    formatter +=
                      "<b>Vol: </b>" + formatCurrency(this.points[1].y, activeCurrencyCode);
                  }
                  return formatter;
                }
              },
              series: [
                {
                  name: "Price",
                  type: "line",
                  data: pricesData,
                  marker: {
                    enabled: chartMode === CHART_MODE.CIRCLE,
                    radius: isPortfolio ? 0 : 2
                  },
                  dataGrouping: {
                    enabled: false
                  }
                },
                {
                  name: "Volume",
                  type: "column",
                  data: totalVolumesData,
                  yAxis: 1,
                  maxPointWidth: 250,
                  dataGrouping: {
                    enabled: false
                  }
                }
              ]
            },
            stockChartOptions(chartContainerId)
          )
        );

        $(this.spinnerTarget).hide();
      });
  }

  updateChart(e) {
    const buttonEl = e.currentTarget;
    const actionType = buttonEl.getAttribute("data-action-type");

    // Clear active on siblings
    const $buttonEl = $(buttonEl);
    $buttonEl.siblings().removeClass("active");
    setTimeout(() => {
      // not sure why setTimeout is needed, otherwise won't catch
      $buttonEl.addClass("active");
    });

    switch (actionType) {
      case ACTION_TYPE.TOGGLE_MODE:
        if (this.chartMode === CHART_MODE.CIRCLE) {
          this.chartMode = CHART_MODE.LINE;
        } else {
          this.chartMode = CHART_MODE.CIRCLE;
        }
        this.renderChart(this.chartDataUrl, this.chartMode);
        break;
      case ACTION_TYPE.MODE_LINE:
        this.chartMode = CHART_MODE.LINE;
        this.renderChart(this.chartDataUrl, this.chartMode);
        break;
      case ACTION_TYPE.TYPE_MARKET_CAP:
        this.chartDataUrl = $(this.toolbarTarget)
          .find(".active")
          .data("market-cap-graph-stats-url");
        this.chartDataType = CHART_DATA_TYPE.MARKET_CAP;
        this.renderChart(this.chartDataUrl, this.chartMode);
        break;
      case ACTION_TYPE.TYPE_PRICE:
        this.chartDataUrl = $(this.toolbarTarget)
          .find(".active")
          .data("graph-stats-url");
        this.chartDataType = CHART_DATA_TYPE.PRICE;
        this.renderChart(this.chartDataUrl, this.chartMode);
        break;
      case ACTION_TYPE.DURATION:
        let dataUrl = null;
        if (this.chartDataType === CHART_DATA_TYPE.PRICE) {
          dataUrl = buttonEl.getAttribute("data-graph-stats-url");
        } else if (this.chartDataType == CHART_DATA_TYPE.MARKET_CAP) {
          dataUrl = buttonEl.getAttribute("data-market-cap-graph-stats-url");
        }
        this.chartDataUrl = dataUrl;
        this.renderChart(this.chartDataUrl, this.chartMode);
        break;
    }
  }

  chartFullScreen(e) {
    const range = e.target.dataset.range;
    const event = new CustomEvent(EventPriceChartReload, {
      bubbles: true,
      cancelable: true
    });
    this.element.dispatchEvent(event);
  }
}



// WEBPACK FOOTER //
// ./app/assets/webpack/controllers/price_chart_controller.js